<template>
  <div id="inspector-panel">
    <div
      v-if="isInspectionPossible() && isUserRole(allowedRoles)"
      id="status-buttons"
    >
      <vf-button
        :busy="selectedStatus === acceptedInspectorStatus && isChangingStatus"
        :disabled="isInspectionDone() || isChangingStatus"
        @click="handleStatusChange(InspectorStatusEnum.accepted)"
      >
        {{ $t('documentDetailView.accept') }}
      </vf-button>

      <vf-button
        id="accept-with-remarks-button"
        :busy="
          selectedStatus === acceptedWithRemarksInspectorStatus &&
          isChangingStatus
        "
        :disabled="file.isFailedInspectionNotificationSent"
        :variant="ButtonVariant.QUATERNARY"
        @click="openRemarksModal(RemarksModalMode.ACCEPT_WITH_REMARKS)"
      >
        {{ $t('documentDetailView.acceptWithRemarks') }}
      </vf-button>

      <vf-button
        id="reject-button"
        :disabled="isInspectionDone() || isChangingStatus"
        :variant="ButtonVariant.SECONDARY"
        @click="openRemarksModal(RemarksModalMode.REJECT)"
      >
        {{ $t('documentDetailView.reject') }}
      </vf-button>
    </div>

    <div v-if="file.inspectorRemarks" id="remarks-container">
      {{ file.inspectorRemarks }}
    </div>

    <remarks-modal
      ref="modalRef"
      :mode="remarksModalMode"
      @remarks-submitted="handleRemarksModalSubmit"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import axios from 'axios';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useRoles } from '@/composables/useRoles';
  import {
    ISupplierDocumentDetails,
    INSPECTION_DONE_STATUSES,
    InspectorStatusEnum,
    Role,
  } from 'types';
  import endpointsList from '../../helpers/endpointsList';
  import RemarksModal from '../RemarksModal/RemarksModal.vue';
  import {
    IRemarksModalEventPayload,
    RemarksModalMode,
  } from '@/components/RemarksModal/RemarksModal.types';
  import { ButtonVariant } from '../VfButton/VfButton.types';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const { isUserRole } = useRoles();

  const emit = defineEmits<{
    (e: 'status-updated'): void;
  }>();

  const props = defineProps<{
    file: ISupplierDocumentDetails;
  }>();

  const remarksModalMode = ref<RemarksModalMode | undefined>(undefined);
  const inspectionDoneStatuses = ref(INSPECTION_DONE_STATUSES);
  const isChangingStatus = ref(false);
  const selectedStatus = ref<InspectorStatusEnum | undefined>(undefined);

  const acceptedInspectorStatus = ref(InspectorStatusEnum.accepted);
  const acceptedWithRemarksInspectorStatus = ref(
    InspectorStatusEnum.acceptedWithRemarks,
  );
  const allowedRoles = ref([Role.Admin, Role.Inspector]);

  async function handleStatusChange(
    statusChageType: InspectorStatusEnum,
    remarks?: string,
  ) {
    isChangingStatus.value = true;
    selectedStatus.value = statusChageType;
    try {
      await axios.patch(
        endpointsList.documentsUploads.assignInspectorStatus(
          props.file._id.toString(),
        ),
        {
          inspectorStatus: statusChageType,
          inspectorRemarks: remarks ?? '',
        },
      );

      createSuccessToast(t('documentDetailView.inspectorStatusChanged'));
    } catch {
      createErrorToast(t('errors.error'));
    }

    isChangingStatus.value = false;
    emit('status-updated');
  }

  function handleRemarksModalSubmit(eventPayload: IRemarksModalEventPayload) {
    handleStatusChange(eventPayload.status, eventPayload.remarks);
  }

  function isInspectionDone(): boolean {
    return (
      props.file &&
      inspectionDoneStatuses.value.includes(props.file?.inspectorStatus)
    );
  }

  function isInspectionPossible(): boolean {
    return (
      [
        InspectorStatusEnum.waiting,
        InspectorStatusEnum.acceptedWithRemarks,
      ].includes(props.file?.inspectorStatus) && !props.file.isReadOnly
    );
  }

  const modalRef = ref<InstanceType<typeof RemarksModal> | null>(null);
  function openRemarksModal(mode: RemarksModalMode): void {
    remarksModalMode.value = mode;
    modalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  #inspector-panel {
    display: flex;
    flex-direction: column;

    #status-buttons {
      margin: 1.5rem 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      row-gap: 1rem;
    }

    #remarks-container {
      margin-top: 1.5rem;
      max-height: 150px;
      margin-bottom: 5px;
      width: 100%;
      border: 1px solid $border-color;
      padding: 10px;
      overflow: auto;
    }
  }
</style>
